import { ParsedUrlQuery } from 'querystring';

import { ById } from '../services/backendService/types';

/**
 * Scope & Sequence (S&S) attribution.
 *
 * Curriculum scope & sequence documents (Google Sheets, and soon the curriculum
 * pages) link to lessons with UTM params:
 *
 *   ?utm_source=scope-and-sequence&utm_campaign={curriculum}&utm_content={variant}&utm_term={lessonType}
 *
 * Lessons opened through such a link are auto-copied into the teacher's library
 * and the attribution is stamped into the copy's template.metadata so it survives
 * further copying/sharing. All keys are flat scalars so they can be spread
 * directly into Mixpanel event payloads.
 */

export const SCOPE_AND_SEQUENCE_UTM_SOURCE = 'scope-and-sequence';

export type ScopeAndSequenceVariant = 'sample-lesson' | 'sample-scope' | 'full';

export interface ScopeAndSequenceParams {
  curriculum: string;
  variant?: ScopeAndSequenceVariant;
  lessonType?: string;
}

const VARIANTS: ScopeAndSequenceVariant[] = ['sample-lesson', 'sample-scope', 'full'];

export const SCOPE_AND_SEQUENCE_VARIANT_OPTIONS: {
  value: ScopeAndSequenceVariant;
  displayName: string;
}[] = [
  { value: 'sample-lesson', displayName: 'Sample lesson (curriculum page)' },
  { value: 'sample-scope', displayName: 'Sample scope & sequence sheet' },
  { value: 'full', displayName: 'Full scope & sequence' },
];

/** Known curriculum slugs — suggestions for the link builder, free text allowed. */
export const SCOPE_AND_SEQUENCE_CURRICULUM_SUGGESTIONS = [
  'bluebonnet',
  'hmh',
  'savvas-myview',
  'wonders',
  'ckla',
  'into-reading',
  'wit-and-wisdom',
];

/** Known lesson-type slugs — suggestions for the link builder, free text allowed. */
export const SCOPE_AND_SEQUENCE_LESSON_TYPE_SUGGESTIONS = [
  'testprep-staar-scr',
  'testprep-staar-ecr',
  'testprep-staar-scr-paired',
  'testprep-staar-ecr-paired',
  'quick-write',
  'vocabulary-practice',
  'grammar-practice',
  'phonics-practice',
  'comprehension',
];

/**
 * Normalize a hand-typed value into a clean slug so Mixpanel properties stay
 * consistent ("Testprep STAAR SCR" -> "testprep-staar-scr").
 */
export function slugifyScopeAndSequenceValue(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Build the full S&S edit-page link for a lesson from the picker values.
 * Returns null when the curriculum slugifies to nothing (e.g. punctuation-only
 * input) — such a link would fail parseScopeAndSequenceParams and neither
 * auto-copy nor attribute.
 */
export function buildScopeAndSequenceLink({
  baseUrl,
  tenantId,
  templateId,
  params,
}: {
  baseUrl: string;
  tenantId: string;
  templateId: string;
  params: ScopeAndSequenceParams;
}): string | null {
  const curriculumSlug = slugifyScopeAndSequenceValue(params.curriculum);
  if (!curriculumSlug) return null;

  const query = new URLSearchParams({
    utm_source: SCOPE_AND_SEQUENCE_UTM_SOURCE,
    utm_campaign: curriculumSlug,
  });
  if (params.variant) query.set('utm_content', params.variant);
  const lessonTypeSlug = params.lessonType
    ? slugifyScopeAndSequenceValue(params.lessonType)
    : '';
  if (lessonTypeSlug) query.set('utm_term', lessonTypeSlug);
  return `${baseUrl}/${tenantId}/lessons/${templateId}/edit?${query.toString()}`;
}

const firstValue = (value: string | string[] | undefined): string | undefined =>
  Array.isArray(value) ? value[0] : value;

export function parseScopeAndSequenceParams(
  query: ParsedUrlQuery,
): ScopeAndSequenceParams | null {
  const source = firstValue(query.utm_source);
  const curriculum = firstValue(query.utm_campaign);
  if (source !== SCOPE_AND_SEQUENCE_UTM_SOURCE || !curriculum) return null;

  const content = firstValue(query.utm_content);
  const variant = VARIANTS.includes(content as ScopeAndSequenceVariant)
    ? (content as ScopeAndSequenceVariant)
    : undefined;

  return {
    curriculum,
    variant,
    lessonType: firstValue(query.utm_term),
  };
}

export type TemplateMetadata = ById<string | boolean | number>;

/** Flat S&S attribution keys stamped into a lesson's template.metadata. */
export type ScopeAndSequenceMetadata = {
  fromScopeAndSequence: true;
  scopeAndSequenceCurriculum: string;
  scopeAndSequenceVariant?: ScopeAndSequenceVariant;
  scopeAndSequenceLessonType?: string;
  scopeAndSequenceSourceTemplateId?: string;
};

/**
 * `sourceTemplateId` is passed when the lesson is a copy of an existing S&S
 * lesson; omitted when the lesson is generated fresh (e.g. the test-prep
 * flow reached through an S&S home-page link).
 */
export function buildScopeAndSequenceMetadata(
  params: ScopeAndSequenceParams,
  sourceTemplateId?: string,
): ScopeAndSequenceMetadata {
  return {
    fromScopeAndSequence: true,
    scopeAndSequenceCurriculum: params.curriculum,
    ...(params.variant ? { scopeAndSequenceVariant: params.variant } : {}),
    ...(params.lessonType ? { scopeAndSequenceLessonType: params.lessonType } : {}),
    ...(sourceTemplateId ? { scopeAndSequenceSourceTemplateId: sourceTemplateId } : {}),
  };
}

/**
 * Flat, top-level event props for Mixpanel. Returns {} for lessons without
 * S&S attribution so callers can always spread the result.
 */
export function getCurriculumTrackingProps(
  metadata: TemplateMetadata | null | undefined,
): TemplateMetadata {
  if (!metadata?.fromScopeAndSequence) return {};
  const props: TemplateMetadata = {
    fromScopeAndSequence: true,
  };
  if (metadata.scopeAndSequenceCurriculum !== undefined) {
    props.scopeAndSequenceCurriculum = metadata.scopeAndSequenceCurriculum;
  }
  if (metadata.scopeAndSequenceVariant !== undefined) {
    props.scopeAndSequenceVariant = metadata.scopeAndSequenceVariant;
  }
  if (metadata.scopeAndSequenceLessonType !== undefined) {
    props.scopeAndSequenceLessonType = metadata.scopeAndSequenceLessonType;
  }
  return props;
}
